/* eslint-disable no-console */
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { format } from 'date-fns';
import { Observable, Subscription, filter, first, map, timer } from 'rxjs';

import { AboutComponent } from './about/about.component';
import { BetsAreOpenComponent } from './bets-are-open/bets-are-open.component';
import { BurnCardsComponent } from './burn-cards/burn-cards.component';
import { ChatComponent } from './chat/chat.component';
import { ClosedTableComponent } from './closed-table/closed-table.component';
import { EnvironmentResolver } from './core/environment-resolver';
import { CountDownComponent } from './count-down/count-down.component';
import { CutCardComponent } from './cut-card/cut-card.component';
import { ErrorPanelComponent } from './error-panel/error-panel.component';
import { GameSelectionComponent } from './game-selection/game-selection.component';
import { GameSwitchComponent } from './game-switch/game-switch.component';
import { ModalComponent } from './modal/modal.component';
import {
    AppPhase,
    DealerConsoleAction,
    FlagStateType,
    GameType,
    NotificationCode,
    NotificationSeverity,
    TableMode
} from './models/communication/app-enums.model';
import { AppEventType, NewRoundPreparationPhaseChangeEvent, NotificationEvent } from './models/communication/app-events.model';
import { AppState } from './models/communication/app-state.model';
import { Notification } from './models/notification.model';
import { NewRoundPreparationComponent } from './new-round-preparation/new-round-preparation.component';
import { NotificationComponent } from './notification/notification.component';
import { GameLabelPipe } from './pipes/game-label/game-label.pipe';
import { RoundVoidedComponent } from './round-voided/round-voided.component';
import { DealerService } from './services/dealer.service';
import { ShoeChangeComponent } from './shoe-change/shoe-change.component';
import { StreamingVideoComponent } from './streaming-video/streaming-video.component';
import { WaitingForPlayersComponent } from './waiting-for-players/waiting-for-players.component';
import { Language } from '../environments/environment.model';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        BurnCardsComponent,
        ClosedTableComponent,
        WaitingForPlayersComponent,
        ShoeChangeComponent,
        NewRoundPreparationComponent,
        RoundVoidedComponent,
        ErrorPanelComponent,
        OverlayModule,
        AboutComponent,
        NotificationComponent,
        CutCardComponent,
        ModalComponent,
        BetsAreOpenComponent,
        GameSwitchComponent,
        GameSelectionComponent,
        GameLabelPipe,
        CountDownComponent,
        ChatComponent,
        StreamingVideoComponent
    ],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
    private static readonly originalWidth = 1920;
    private static readonly originalHeight = 1080;
    private static readonly notificationDurationMs = 3000;
    private static readonly gameChangeDelayMs = 50;

    @ViewChild('container') containerEl!: ElementRef<HTMLElement>;

    protected viewportScaling = {
        enabled: true,
        fillWidth: true,
        fillHeight: true
    };

    protected containerHeight = AppComponent.originalHeight;
    protected containerWidth = AppComponent.originalWidth;

    protected appIsHidden = false;
    protected aboutDialogVisible = false;
    protected selectGameVisible = false;
    protected menuVisible = false;

    protected notification: Notification = {
        show: false,
        text: '',
        severity: NotificationSeverity.Error,
        timeoutId: undefined,
        duration: AppComponent.notificationDurationMs,
        canClose: false
    };

    protected readonly AppPhase = AppPhase;
    protected readonly TableMode = TableMode;

    protected time$: Observable<string> = this.createTimeObservable();
    protected state$!: Observable<AppState>;
    protected version: string;
    protected languages: Language[] = [];

    protected isVideoHidden = false;
    protected supervisorMode = false;

    protected cutCardDrawn = false;

    private subscription?: Subscription;

    constructor(
        private readonly environment: EnvironmentResolver,
        private readonly dealerService: DealerService,
        private readonly route: ActivatedRoute
    ) {
        const { version, languages } = this.environment.getEnvironment();
        this.version = version;
        this.languages = languages || [];
    }

    ngOnInit(): void {
        this.state$ = this.dealerService.getStateObservable().pipe(filter((state): state is AppState => !!state));
        this.state$.pipe(first()).subscribe(() => {
            // Timeout because - TypeError: Cannot read properties of undefined (reading 'nativeElement')
            if (this.viewportScaling.enabled) {
                setTimeout(() => {
                    this.scaleViewport(window.innerWidth, window.innerHeight);
                });
            }
        });

        this.subscription = this.dealerService.getEventsObservable().subscribe((event) => {
            if (event.name === AppEventType.CutCardDrawn) {
                this.cutCardDrawn = true;
                this.handleCutCardDrawnEvent();
            } else if (event.name === AppEventType.Notification) {
                this.handleNotification(event.data as NotificationEvent);
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            } else if (this.cutCardDrawn && (event.data as NewRoundPreparationPhaseChangeEvent).phase === AppPhase.NewRoundPreparation) {
                this.cutCardDrawn = false;
                this.hideNotification();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    async onFlagGameClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.RoundFlagged);
    }

    async onFreeGameClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.RoundBetsFree);
    }

    async onCallSupervisorClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.SupervisorCalled);
    }

    async onCloseTableClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.TableClosingRequested);
    }

    async onEnableChatClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.ChatEnabled);
    }

    async onDisableChatClick(button: HTMLButtonElement): Promise<void> {
        await this.onActionClick(button, FlagStateType.ChatEnabled, false);
    }

    async onShoeChangedClick(button: HTMLButtonElement): Promise<void> {
        try {
            this.setButtonDisabledState(button, true);
            await this.dealerService.sendDealerActionRequest(DealerConsoleAction.ShoeChanged);
        } catch (error) {
            this.setButtonDisabledState(button, false);
            console.error(error);
            this.showNotification($localize`Action failed`, NotificationSeverity.Error, true);
        }
    }

    async onVoidClick(button: HTMLButtonElement): Promise<void> {
        try {
            this.setButtonDisabledState(button, true);
            await this.dealerService.sendDealerActionRequest(DealerConsoleAction.ChangeShoe);
        } catch (error) {
            this.setButtonDisabledState(button, false);
            console.error(error);
            this.showNotification($localize`Action failed`, NotificationSeverity.Error, true);
        }
    }

    async onGameSelect(game: GameType): Promise<void> {
        try {
            this.appIsHidden = true;
            await this.dealerService.sendChangeGameRequest(game);
            setTimeout(() => (this.appIsHidden = false), AppComponent.gameChangeDelayMs);
        } catch (error) {
            this.appIsHidden = false;
            console.error(error);
            this.showNotification($localize`Action failed`, NotificationSeverity.Error, true);
        }
    }

    protected scaleViewport(innerWidth: number, innerHeight: number): void {
        const originalProportion = AppComponent.originalWidth / AppComponent.originalHeight;

        const windowProportion = innerWidth / innerHeight;
        const proportionRatio = windowProportion / originalProportion;

        const scale = proportionRatio < 1 ? innerWidth / AppComponent.originalWidth : innerHeight / AppComponent.originalHeight;

        if (this.viewportScaling.fillWidth) {
            this.containerWidth = Math.max(proportionRatio, 1) * AppComponent.originalWidth;
        }

        if (this.viewportScaling.fillHeight) {
            this.containerHeight = AppComponent.originalHeight + AppComponent.originalHeight * (1 - Math.min(proportionRatio, 1));
        }

        this.containerEl.nativeElement.style.transform = `scale(${scale})`;
        this.containerEl.nativeElement.style.width = `${this.containerWidth}px`;
        this.containerEl.nativeElement.style.height = `${this.containerHeight}px`;
    }

    protected scaleContainer(event: UIEvent): void {
        if (this.viewportScaling.enabled) {
            this.scaleViewport((event.target as Window).innerWidth, (event.target as Window).innerHeight);
        }
    }

    protected showNotification(text = 'Error', severity = NotificationSeverity.Error, canClose = false): void {
        if (this.notification.timeoutId) {
            clearTimeout(this.notification.timeoutId);
        }

        this.notification = {
            ...this.notification,
            show: true,
            text,
            severity,
            canClose
        };

        if (!canClose) {
            this.notification.timeoutId = setTimeout(() => this.hideNotification(), this.notification.duration);
        }
    }

    protected hideNotification(): void {
        this.notification = {
            ...this.notification,
            show: false
        };
    }

    protected changeLanguage(languageCode: string): void {
        window.location.href = `/${languageCode}/${window.location.search}`;
    }

    private getNotificationText(code: NotificationCode): string {
        switch (code) {
            case NotificationCode.AuthenticationFailed:
                return $localize`Authentication failed!`;
            case NotificationCode.AuthenticationSuccess:
                return $localize`Successfully authenticated!`;
            case NotificationCode.CroupierChangeInvalidState:
                return $localize`Croupier change not allowed in this game state.`;
            case NotificationCode.DuplicateCard:
                return $localize`Same round, same card detected.`;
            case NotificationCode.FlagFailed:
                return $localize`Flag failed, round not marked.`;
            case NotificationCode.InvalidCard:
                return $localize`Invalid card or card not recognized.`;
            case NotificationCode.InvalidCroupierId:
                return $localize`Invalid croupier ID for this table!`;
            case NotificationCode.ScannerDisconnected:
                return $localize`Card scanner is disconnected!`;
            case NotificationCode.ScannerConnected:
                return $localize`Card scanner is connected and ready.`;
            default:
                // eslint-disable-next-line no-console
                console.warn(`Unknown notification code: ${code as string}`);
                return code;
        }
    }

    private async onActionClick(button: HTMLButtonElement, flag: FlagStateType, state = true): Promise<void> {
        try {
            this.setButtonDisabledState(button, true);
            await this.dealerService.sendSetFlagStateRequest(flag, state);
            if (flag === FlagStateType.ChatEnabled) {
                this.setButtonDisabledState(button, false);
            }
        } catch (error) {
            this.setButtonDisabledState(button, false);
            console.error(error);
            this.showNotification($localize`Action failed`, NotificationSeverity.Error, true);
        }
    }

    private setButtonDisabledState(button: HTMLButtonElement, disabled: boolean): void {
        // eslint-disable-next-line no-param-reassign
        button.disabled = disabled;
    }

    private createTimeObservable(): Observable<string> {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return timer(0, 1000).pipe(map(() => format(new Date(), 'HH:mm:ss')));
    }

    private handleCutCardDrawnEvent(): void {
        this.showNotification($localize`Cut card drawn`, NotificationSeverity.Error, true);
    }

    private handleNotification(event: NotificationEvent): void {
        const canClose = event.severity !== NotificationSeverity.Info;
        this.showNotification(this.getNotificationText(event.code), event.severity, canClose);
    }
}
